import { Country, CountryDetails, State, City } from './../../types/openApi';
import axios, { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { ALLOWED_COUNTRIES, geoLocationCdnUrl, locationApiKey } from '../../config/constants';
import { sessionStorageGetItem, sessionStorageSetItem } from '../../storage/storageManager';

const headers = {
    'X-CSCAPI-KEY': locationApiKey,
};

const CDN_BASE = geoLocationCdnUrl;

const filterAllowedCountries = (countries: Country[]): Country[] => {
    if (ALLOWED_COUNTRIES.length === 0) {
        return countries;
    }

    return countries.filter(country => {
        const isAllowedByIso2 = ALLOWED_COUNTRIES.length === 0 || ALLOWED_COUNTRIES.includes(country.iso2);
        return isAllowedByIso2;
    });
};

async function fetchFromCDN<T>(path: string): Promise<T | null> {
    try {
        const res = await axios.get<T>(`${CDN_BASE}${path}`);
        return res.data;
    } catch (err) {
        console.warn(`CDN failed for: ${path}`, err);
        return null;
    }
}

export const OpenAPIService = {
    async fetchCountriesWithPhoneCode(): Promise<Country[]> {
        try {

             const cdnData = await fetchFromCDN<Country[]>('/cdn/data/countries.json');
            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                const filteredData = filterAllowedCountries(cdnData);
                sessionStorageSetItem('countries', filteredData, false);
                return filteredData;
            }
            // Fetch the list of countries
            const countriesResponse: AxiosResponse<Country[]> = await axios.get(
                'https://api.countrystatecity.in/v1/countries',
                {
                    headers,
                }
            );

            const countriesResult = countriesResponse.data;

            // Fetch country details and phone code for each country in parallel
            const mapItems = await Promise.all(
                countriesResult.map(async (item: Country) => {
                    const countryResponse: AxiosResponse<CountryDetails> = await axios.get(
                        `https://api.countrystatecity.in/v1/countries/${item.iso2}`,
                        {
                            headers,
                        }
                    );

                    const phoneCodeData = countryResponse.data;

                    return {
                        ...item,
                        phonecode: phoneCodeData?.phonecode?.replace('+', ''),
                    };
                })
            );

            sessionStorageSetItem('countries', mapItems, false);

            return mapItems;
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },

    async fetchAllStates(): Promise<State[]> {
        try {
            // Fetch the list of states
            const cdnData = await fetchFromCDN<State[]>('/cdn/data/states.json');
            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                sessionStorageSetItem('states', cdnData, false);
                return cdnData;
            }

            const stateResponse: AxiosResponse<State[]> = await axios.get(`https://api.countrystatecity.in/v1/states`, {
                headers,
            });
            const stateResult = stateResponse.data;

            sessionStorageSetItem('states', stateResult, false);

            return stateResult;
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },

    async fetchStatesByCountry(Iso2CountryString?: string): Promise<State[]> {
        try {
             const cdnData = await fetchFromCDN<State[]>(`/cdn/data/states-by-country/${Iso2CountryString}.json`);
            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                return cdnData;
            }

            // Fetch the list of states
            const stateResponse: AxiosResponse<State[]> = await axios.get(
                `https://api.countrystatecity.in/v1/countries/${Iso2CountryString}/states`,
                {
                    headers,
                }
            );
            const stateResult = stateResponse.data;

            // sessionStorage.setItem('states', JSON.stringify(stateResult));

            return stateResult;
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },

    async fetchAllCities(iso2CountryString: string): Promise<City[]> {
          if (!iso2CountryString) {
            throw new Error('Country ISO2 code is required');
        }
        try {
            const cdnData = await fetchFromCDN<City[]>(`/cdn/data/cities-by-country/${iso2CountryString}.json`);
            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                const existingData = sessionStorageGetItem('cities', false) || [];
                const uniqueNewRecords = cdnData.filter(
                    (newRecord) => !existingData.some((existingRecord: any) => existingRecord.id === newRecord.id)
                );
                const updatedData = [...existingData, ...uniqueNewRecords];
                sessionStorageSetItem('cities', updatedData, false);

                return updatedData;
            }

            // Fetch the list of cities
            const citiesResponse: AxiosResponse<City[]> = await axios.get(
                `https://api.countrystatecity.in/v1/countries/${iso2CountryString}/cities`,
                {
                    headers,
                }
            );

            const cityResult = citiesResponse.data;

            const existingData = JSON.parse(sessionStorage.getItem('cities')!) || [];

            const uniqueNewRecords = cityResult.filter(
                (newRecord) => !existingData.some((existingRecord: any) => existingRecord.id === newRecord.id)
            );

            const updatedData = [...existingData, ...uniqueNewRecords];

            sessionStorage.setItem('cities', JSON.stringify(updatedData));

            return updatedData;
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },

    async fetchCitiesByStateAndCountry(iso2CountryString: string, iso2StateString?: string): Promise<City[]> {
         if (!iso2CountryString || !iso2StateString) {
            throw new Error('Both country and state ISO2 codes are required');
        }
        try {

            const cdnData = await fetchFromCDN<City[]>(`/cdn/data/cities-by-state/${iso2CountryString}/${iso2StateString}.json`);
            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                return cdnData;
            }

            // Fetch the list of cities
            const citiesResponse: AxiosResponse<City[]> = await axios.get(
                `https://api.countrystatecity.in/v1/countries/${iso2CountryString}/states/${iso2StateString}/cities`,
                {
                    headers,
                }
            );

            const cityResult = citiesResponse.data;

            // sessionStorage.setItem('cities', JSON.stringify(cityResult));

            return cityResult;
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },

    async fetchCountriesCodeAndPhoneCode(countryName: string): Promise<Country | null> {
        try {
           const cdnData = await fetchFromCDN<Country[]>('/cdn/data/countries.json');
            let countriesResult: Country[];

            if (cdnData && Array.isArray(cdnData) && cdnData.length > 0) {
                countriesResult = cdnData;
            } else {
                const countriesResponse: AxiosResponse<Country[]> = await axios.get(
                    'https://api.countrystatecity.in/v1/countries',
                    {
                        headers,
                    }
                );
                countriesResult = countriesResponse.data;
            }

            const countryNameToFind = String(countryName).trim().toLowerCase();

            const matchedCountry = countriesResult.find((country) => {
                const result = country.name.trim().toLowerCase() === countryNameToFind;
                return result;
            });

            if (!matchedCountry) {
                toast.error(`${countryNameToFind} country not found.`);
                return null;
            }

            if (matchedCountry) {
                const countryResponse: AxiosResponse<CountryDetails> = await axios.get(
                    `https://api.countrystatecity.in/v1/countries/${matchedCountry.iso2}`,
                    {
                        headers,
                    }
                );

                const phoneCodeData = countryResponse.data;

                return {
                    ...matchedCountry,
                    phonecode: phoneCodeData?.phonecode?.replace('+', ''),
                };
            } else {
                return null;
            }
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
            throw error;
        }
    },
};
